import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TtScreenBlockService } from '@app/core/components/tt-screen-block/tt-screen-block.service';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { FileService } from '@app/core/services/file.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocArcGet, DocArcService, DocumentCategoryItem, GetDocArcParams } from '@app/services/doc-arc.service';

@Component({
    selector: 'tt-doc-arc',
    templateUrl: './doc-arc.component.html',
    styleUrls: ['./doc-arc.component.css'],
    encapsulation: ViewEncapsulation.None,

    standalone: false,
})
export class DocArcComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: DocArcParams;

    public page?: DocArcPage;

    public mobileViewerPage?: DocArcPage;

    public documentCategories: DocumentCategoryItem[] = [];

    public readonly bottomSheetMinHeight = '10rem';

    private deregisterOfficeConversion?: () => void;

    private conversionTimeout?: number;

    constructor(
        private docarc: DocArcService,
        private file: FileService,
        private appSettings: AppSettingsService,
        private screenBlock: TtScreenBlockService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
        @Optional() @Inject('$rootScope') private rootScope?: LegacyRootScope,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    private async init(): Promise<void> {
        if (!this.params) return;

        this.screenBlock.start();

        try {
            await this.appSettings.ensureIsReady();

            const getParams: GetDocArcParams = { docarc_keyno: this.params.docarc_keyno };
            const [docarc, documentCategories] = await Promise.all([this.docarc.getDocArc(getParams), this.docarc.getDocumentCategories()]);
            const file = await this.file.getFileDetails({ fileName: docarc.docfilename, maxWidth: 1200, maxHeight: 600 });

            this.documentCategories = documentCategories;
            this.page = { ...file, ...docarc };
            this.mobileViewerPage = this.getMobileViewerPage(this.page);
            this.navigator?.setHeading?.(docarc.doctitle || docarc.docfilename);
        } finally {
            this.screenBlock.stop();
        }
    }

    private getMobileViewerPage(page: DocArcPage): DocArcPage {
        if (!page.imageLink || !page.docfilename.toLowerCase().endsWith('.pdf')) return page;

        const [url, fragment = ''] = page.imageLink.split('#', 2);
        const parameters = new URLSearchParams(fragment);
        parameters.set('view', 'FitH');

        return { ...page, imageLink: `${url}#${parameters.toString()}` };
    }

    ngOnInit(): void {
        this.subscribeToOfficeConversion();

        if (this.navigator) {
            this.params = this.navigator.params as unknown as DocArcParams;
            void this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) void this.init();
    }

    ngOnDestroy(): void {
        this.deregisterOfficeConversion?.();

        if (this.conversionTimeout !== undefined) window.clearTimeout(this.conversionTimeout);
    }

    private subscribeToOfficeConversion(): void {
        this.deregisterOfficeConversion = this.rootScope?.$on('ieOfficeOnline-onConvert', (_event, data) => {
            void this.pollOfficeConversion({ ...data, fileItemKeyno: '0' });
        });
    }

    private async pollOfficeConversion(data: OfficeConversionEvent): Promise<void> {
        const result = await this.file.isConvertCompleted({ fileItemId: data.fileItemId, fileItemKeyno: data.fileItemKeyno ?? '0' });

        if (result.isCompleted) {
            if (this.page) {
                await this.docarc.updateDocArcFileName({ docarcKeyno: this.page.docarc_keyno, docFileName: result.fileName });
            }

            return;
        }

        this.conversionTimeout = window.setTimeout(() => {
            void this.pollOfficeConversion({ ...data, fileItemKeyno: result.fileItemKeyno });
        }, 200);
    }
}

export interface DocArcParams {
    argtype?: string;
    argvalue?: string;
    docarc_keyno: number | `${number}`;
}

export interface DocArcPage extends DocArcGet {
    fileName?: string | null;
    imageLink?: string | null;
    contentType?: string | null;
    localFileName?: string | null;
    thumbnailLink?: string | null;
    wopiIsConverted?: boolean | null;
}

interface OfficeConversionEvent {
    fileItemId: string;
    fileItemKeyno?: string;
}

interface LegacyRootScope {
    $on(eventName: string, listener: (event: unknown, data: OfficeConversionEvent) => void): () => void;
}
